import { useExperienceLogo } from '@/features/experiences/hooks/useExperienceLogo';
import { Button, Flex, Icon, IconButton, Image } from '@/ui';
import { SidebarIconChip, SidebarRow, SidebarRowLabel } from '../SidebarKit';

const THUMBNAIL_SIZE = '26px';

type Props = {
  /**
   * Whether the logo belongs to a planner the views share. A single experience
   * owns its own, so promising it to "every view" would name views it has none
   * of.
   */
  isSharedAcrossViews: boolean;
};

/**
 * Uploads the author's logo from the Views panel, under the Logo switch that
 * turns it on. The only way to add one: the header slot offers replace and
 * remove, but nothing to fill an empty slot with (AH-2544).
 *
 * The image is the owning planner's, so this row edits one picture every view
 * draws — unlike the switch above it, which is per view.
 */
export const LogoImageRow = ({ isSharedAcrossViews }: Props) => {
  const { preview, isUploading, isRemoving, pickLogo, removeLogo } =
    useExperienceLogo();

  return (
    <SidebarRow>
      <Flex align="center" gap={2.5} minW={0}>
        {preview ? (
          <Image
            alt=""
            borderRadius="ah-md"
            boxSize={THUMBNAIL_SIZE}
            flexShrink={0}
            objectFit="cover"
            opacity={isRemoving ? 0.5 : 1}
            src={preview}
          />
        ) : (
          <SidebarIconChip size={THUMBNAIL_SIZE}>
            <Icon color="ink.soft" icon="Image" size={3.5} />
          </SidebarIconChip>
        )}
        <SidebarRowLabel
          label="Logo image"
          sublabel={isSharedAcrossViews ? 'Shared by every view' : undefined}
        />
      </Flex>
      <Flex align="center" flexShrink={0} gap={1} ml={2}>
        {/* Both actions write the same field, so each one waits out the other
            rather than letting request order decide the outcome. */}
        <Button
          isDisabled={isRemoving}
          isLoading={isUploading}
          size="ah-xs"
          variant="ahSecondary"
          onClick={pickLogo}
        >
          {preview ? 'Replace' : 'Upload'}
        </Button>
        {preview && (
          <IconButton
            aria-label="Remove logo"
            icon={<Icon icon="X" size={3} />}
            isDisabled={isUploading}
            isLoading={isRemoving}
            size="ah-xs"
            variant="ahGhost"
            onClick={removeLogo}
          />
        )}
      </Flex>
    </SidebarRow>
  );
};
